// Audit Logs Page — renders REAL audit_logs rows from the backend.
// Backend: GET /api/v1/users/audit-logs → { data: AuditLog[], pagination: { total } }
// over the audit_logs table. No mock/synthetic fallback — on failure we show an
// honest error and an empty table.
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import { Icon } from '@/components/shared/Icon';
import { toCsv } from '@/lib/csv';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';

// Mirrors the real audit_logs columns (no severity/actor_email/metadata — those
// don't exist in the schema).
interface AuditLog {
  id: string;
  created_at: string;
  user_id: string | null;
  api_key_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  changes: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
}

const ACTION_ICONS: Record<string, { name: string; label: string }> = {
  'license.create': { name: 'key-round', label: 'License created' },
  'license.revoke': { name: 'ban', label: 'License revoked' },
  'license.suspend': { name: 'pause', label: 'License suspended' },
  'license.reactivate': { name: 'play', label: 'License reactivated' },
  'license.validate': { name: 'check', label: 'License validated' },
  'license.update': { name: 'pencil', label: 'License updated' },
  'license.update_seats': { name: 'users', label: 'License seats updated' },
  'license.expire': { name: 'clock', label: 'License expired' },
  'license.renew': { name: 'refresh-cw', label: 'License renewed' },
  'api_key.create': { name: 'key-round', label: 'API key created' },
  'api_key.revoke': { name: 'trash-2', label: 'API key revoked' },
  'user.invite': { name: 'mail', label: 'User invited' },
  'user.update': { name: 'user-cog', label: 'User updated' },
  'user.deactivate': { name: 'user-x', label: 'User deactivated' },
};

function actorLabel(log: AuditLog): { id: string; kind: string } {
  if (log.user_id) return { id: log.user_id, kind: 'admin user' };
  if (log.api_key_id) return { id: log.api_key_id, kind: 'api key' };
  return { id: 'system', kind: 'system' };
}

export function AuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const actionFilter = searchParams.get('action') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 25;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.set('action', actionFilter);
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      const resp = await apiRequest<{ data: AuditLog[]; pagination: { total: number } }>(
        `/users/audit-logs?${params}`,
      );
      setLogs(Array.isArray(resp?.data) ? resp.data : []);
      setTotal(resp?.pagination?.total ?? 0);
    } catch (err) {
      setLogs([]);
      setTotal(0);
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const totalPages = Math.ceil(total / pageSize);

  // Client-side text filter over the loaded page (action / resource / ip / actor
  // / changes).
  //
  // `changes` is included because it is where this system records the SUBJECT of
  // an action whenever that subject has no UUID to put in resource_id: the
  // human-readable VW- licence key (license.routes.ts:240), the tprm7 org id and
  // domain (trust-provisioning), and the published rate-card version. Without it
  // an operator searching 'FY26.1' or a licence key gets zero hits while the row
  // sits on screen — which reads as "that action was never audited".
  const visible = search.trim()
    ? logs.filter((l) => {
        const q = search.toLowerCase();
        return (
          l.action?.toLowerCase().includes(q) ||
          (l.resource_type || '').toLowerCase().includes(q) ||
          (l.resource_id || '').toLowerCase().includes(q) ||
          (l.ip_address || '').toLowerCase().includes(q) ||
          (l.user_id || '').toLowerCase().includes(q) ||
          JSON.stringify(l.changes ?? '').toLowerCase().includes(q)
        );
      })
    : logs;

  // Action filter options derived from the real loaded rows (no synthetic catalog).
  const uniqueActions = Array.from(new Set(logs.map((l) => l.action).filter(Boolean))).sort();

  const exportCsv = () => {
    // `changes` is exported for the same reason it is searched: for any action
    // whose subject has no UUID, it is the ONLY column that names what was acted
    // on. Omitting it produced audit evidence in which 'price_book.publish' rows
    // did not say which rate card was published.
    const header = ['timestamp', 'actor', 'action', 'resource_type', 'resource_id', 'ip_address', 'changes'];
    // Through the shared writer (lib/csv.ts): quoted, and a cell that starts with = + - @ is
    // neutralised. These columns hold text an outsider can influence.
    const csv = toCsv(
      header,
      visible.map((l) => [
        l.created_at, actorLabel(l).id, l.action, l.resource_type || '', l.resource_id || '', l.ip_address || '',
        l.changes ? JSON.stringify(l.changes) : '',
      ]),
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-page-${page}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">Complete audit trail of all system actions</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={visible.length === 0}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: visible.length === 0 ? 'not-allowed' : 'pointer', opacity: visible.length === 0 ? 0.5 : 1, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" placeholder="Filter this page: action, resource, IP, actor…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <select value={actionFilter} onChange={e => { const p = new URLSearchParams(searchParams); if (e.target.value) p.set('action', e.target.value); else p.delete('action'); p.set('page', '1'); setSearchParams(p); }} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--surface)', cursor: 'pointer', maxWidth: '220px' }}>
          <option value="">All actions</option>
          {uniqueActions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: '#FEE2E2', color: '#DC2626', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* Log Table */}
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              {['Timestamp', 'Actor', 'Action', 'Resource', 'IP'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>No audit logs found</td></tr>
            ) : visible.map(log => {
              const actor = actorLabel(log);
              const actionIcon = ACTION_ICONS[log.action] || { name: 'file-text', label: 'Audit event' };
              return (
                <React.Fragment key={log.id}>
                  <tr
                    style={{ borderBottom: expandedId === log.id ? 'none' : '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-secondary)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 16px', fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, fontFamily: 'monospace' }}>{actor.id}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{actor.kind}</div>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span role="img" aria-label={actionIcon.label} style={{ display: 'inline-flex' }}>
                          <Icon name={actionIcon.name} size={14} />
                        </span>
                        <code style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{log.action}</code>
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ fontSize: '12px' }}>
                        <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{log.resource_type || '—'}</span>
                        {log.resource_id && <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '4px' }}>{log.resource_id}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                      {log.ip_address || '—'}
                    </td>
                  </tr>
                  {expandedId === log.id && (
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td colSpan={5} style={{ padding: '12px 16px 16px', background: 'var(--bg-secondary)' }}>
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '6px' }}>Changes</div>
                            <pre style={{ fontSize: '11px', fontFamily: 'monospace', background: 'var(--surface)', padding: '8px', borderRadius: '6px', margin: 0, overflowX: 'auto' }}>
                              {JSON.stringify(log.changes, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.user_agent && (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            User Agent: {log.user_agent}
                          </div>
                        )}
                        {!log.changes && !log.user_agent && (
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No additional detail recorded.</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <span>Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button disabled={page <= 1} onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page - 1)); setSearchParams(p); }} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>
                <ChevronLeft size={14} />
              </button>
              <button disabled={page >= totalPages} onClick={() => { const p = new URLSearchParams(searchParams); p.set('page', String(page + 1)); setSearchParams(p); }} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
